import { useEffect, useState, useContext } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, User, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';

function Requests() {
  const { API } = useContext(AppContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/transactions/pending`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await axios.post(`${API}/transactions/${transactionId}/accept`);
      toast.success('Request accepted! You can now complete the service.');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    } finally {
      setProcessing(null);
    }
  };

  const handleRefuse = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await axios.post(`${API}/transactions/${transactionId}/refuse`);
      toast.success('Request refused');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refuse request');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading requests...</div>;
  }

  return (
    <div className="space-y-6" data-testid="requests">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Incoming Requests</h1>
        <p className="text-gray-600">Manage service and good requests from other users</p>
      </div>

      {requests.length === 0 ? (
        <Card className="bg-white shadow-lg">
          <CardContent className="py-12">
            <p className="text-center text-gray-500">No pending requests</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requests.map((request) => (
            <Card key={request.id} className="bg-white shadow-lg" data-testid={`request-${request.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    Pending
                  </Badge>
                  <div className="flex items-center gap-1 text-purple-600 font-semibold">
                    <Clock className="w-4 h-4" />
                    <span>{request.time_tokens}h</span>
                  </div>
                </div>
                <CardTitle className="mt-2">{request.service_title}</CardTitle>
                <CardDescription className="flex items-center gap-1 text-sm">
                  <User className="w-3 h-3" />
                  Requested by: {request.from_user}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  Requested on: {new Date(request.timestamp).toLocaleString()}
                </p>
              </CardContent>
              <CardFooter className="flex gap-2">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => handleAccept(request.id)}
                  disabled={processing === request.id}
                  data-testid={`accept-${request.id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleRefuse(request.id)}
                  disabled={processing === request.id}
                  data-testid={`refuse-${request.id}`}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Refuse
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default Requests;